import { useEffect, useState } from "react";
import axios from "axios";
import { Check, AlertTriangle, X, Loader2, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Item = ({ id, label, ok, warn, detail }) => {
  const Icon = ok ? Check : warn ? AlertTriangle : X;
  const tone = ok ? "bg-[#0a0a0b] text-white" : warn ? "bg-[#ffb700]/20 text-[#8a5a00]" : "bg-[#ff2a85]/10 text-[#ff2a85]";
  return (
    <li data-testid={`readiness-${id}`} className="flex items-start gap-3">
      <span className={`h-6 w-6 shrink-0 rounded-full flex items-center justify-center ${tone}`}><Icon size={12} strokeWidth={3} /></span>
      <div className="min-w-0">
        <p className="text-sm font-medium leading-6">{label}</p>
        {detail && <p data-testid={`readiness-${id}-detail`} className="text-[11px] text-[#5f5e5a] leading-relaxed">{detail}</p>}
      </div>
    </li>
  );
};

export const ReadinessPanel = ({ headers }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const load = () => { setData(null); setErr(false); axios.get(`${API}/admin/readiness`, { headers }).then((r) => setData(r.data)).catch(() => setErr(true)); };
  useEffect(load, [headers]);

  const s = data?.stripe || {}, p = data?.paypal || {}, e = data?.email || {};
  const live = !!data?.live;
  return (
    <section data-testid="readiness-panel" className={`rounded-3xl border p-5 sm:p-6 mb-6 ${live ? "border-[#0a0a0b] bg-[#0a0a0b] text-white" : "border-[#e3e2dc] bg-[#faf9f6]"}`}>
      <div className="flex items-center justify-between gap-4 mb-4">
        <div>
          <p className={`font-mono-d text-[10px] uppercase tracking-[0.25em] ${live ? "text-[#ff2a85]" : "text-[#5f5e5a]"}`}>Go-live checklist</p>
          <p data-testid="readiness-headline" className="font-display font-extrabold uppercase text-lg leading-tight mt-1">
            {!data ? (err ? "Couldn't check" : "Checking…") : live ? "Ready for real customers" : "Running in test mode"}
          </p>
        </div>
        <button data-testid="readiness-refresh" onClick={load} className={`h-9 w-9 rounded-full border flex items-center justify-center ${live ? "border-white/20 hover:border-white" : "border-[#e3e2dc] hover:border-[#0a0a0b]"}`} aria-label="Re-check">
          {data || err ? <RefreshCw size={13} /> : <Loader2 size={13} className="animate-spin" />}
        </button>
      </div>
      {data && (
        <ul className={`grid sm:grid-cols-3 gap-4 ${live ? "[&_p]:text-white [&_li_p+p]:text-white/60" : ""}`}>
          <Item id="stripe" label={`Stripe · ${s.mode === "live" ? "LIVE" : "test mode"}`} ok={s.ok && s.mode === "live" && s.charges_enabled} warn={s.ok && s.mode !== "live"}
            detail={!s.configured ? (s.error ? `Stripe key problem: ${s.error}. Fix it under Re-publish → Secrets → STRIPE_API_KEY.` : "No Stripe key saved — add STRIPE_API_KEY (sk_live_…) under Re-publish → Secrets.") : s.ok === false ? `Key rejected: ${s.error}` : [s.account, s.charges_enabled ? "charges on" : "charges OFF — finish Stripe onboarding", s.wallets?.length ? `${s.wallets.map((w) => w.replace("_", " ")).join(", ")} on` : "wallets off", s.webhook ? "webhook registered" : "webhook registers on first checkout"].join(" · ")} />
          <Item id="paypal" label={`PayPal · ${!p.configured ? "not set up" : p.mode === "live" ? "LIVE" : "sandbox"}`} ok={p.configured && p.ok && p.mode === "live"} warn={!p.configured || (p.ok && p.mode !== "live")}
            detail={!p.configured ? "Optional — card checkout still works without it." : p.ok === false ? `Credentials rejected: ${p.error}` : p.mode === "live" ? "Live credentials verified." : "Sandbox credentials verified — switch PAYPAL_MODE to live for real payments."} />
          <Item id="email" label="Order emails · Brevo" ok={e.ok} warn={false}
            detail={!e.configured ? "No Brevo key saved — confirmations won't send." : e.ok ? `Sending from ${e.sender}${e.credits != null ? ` · ${e.credits} credits left` : ""}` : `Brevo rejected the key${e.error ? `: ${e.error}` : ""}`} />
        </ul>
      )}
      {data && !live && (
        <p data-testid="readiness-hint" className="mt-4 text-[11px] text-[#5f5e5a] leading-relaxed">
          Live keys are read from Manage → Secrets when you publish. This preview intentionally stays in test mode so you can place trial orders safely.
        </p>
      )}
    </section>
  );
};
